import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import { putFileWithProgress, type UploadProgressHandler } from './fileUpload';

// ---------------------------------------------------------------------------
// Clients CRM module (CLE-79) — API client + colocated types.
//
// Mirrors the backend contract in backend/src/routes/clients.ts. The Clients
// module is DB-first (real CRUD), so these calls write directly. Response
// envelopes follow the app-wide { success, data, meta } shape.
// ---------------------------------------------------------------------------

export const RESOURCE_CATEGORIES = ['CONTRACT', 'INVOICE', 'PROPOSAL', 'NDA', 'OTHER'] as const;
export type ResourceCategory = (typeof RESOURCE_CATEGORIES)[number];

export interface ClientSummary {
  id: string;
  name: string;
  shortName: string | null;
  cifCui: string | null;
  regCom: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  country: string | null;
  contactPerson: string | null;
  avatarUrl: string | null;
  // Content-calendar colour (#rrggbb); null = picked from the palette.
  color: string | null;
  // The client's own container for work with no real project, once created.
  defaultProjectId: string | null;
  isActive: boolean;
  // Multi-company: firm tag (NULL = default firm).
  companyId: string | null;
  company: { id: string; name: string } | null;
  // Industria (Medical, Dental…); same industry = competitors (Actori).
  industryId: string | null;
  industry: { id: string; name: string } | null;
  projectCount: number;
  invoiceCount: number;
  totalInvoiced: number;
  totalPaid: number;
  outstanding: number;
  currency: string;
  // Received from this client per confirmed bank-statement lines.
  totalReceived: number;
  totalReceivedCurrency: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClientContact {
  id: string;
  clientId: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClientResource {
  id: string;
  clientId: string;
  name: string;
  description: string | null;
  category: ResourceCategory;
  fileType: string | null;
  fileSize: number | null;
  s3Key: string;
  s3Url: string | null;
  uploadedAt: string;
}

export interface ClientMeetingNote {
  id: string;
  clientId: string;
  title: string;
  date: string;
  attendees: string | null;
  content: string;
  summary: string | null;
  actionItems: string | null;
  source: string;
  s3Key: string | null;
  s3Url: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClientProject {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: string;
  health: string | null;
  startDate: string | null;
  endDate: string | null;
  budget: number | null;
  /** Currency the budget is in (RON | EUR). */
  budgetCurrency: string;
}

export interface ClientFinancials {
  currency: string;
  totalInvoiced: number;
  totalPaid: number;
  outstanding: number;
}

export interface AggregatedInvoice {
  id: string;
  source: 'db' | 'smartbill';
  invoiceNumber: string;
  direction: 'INCOME' | 'OUTCOME';
  status: string;
  amount: number;
  paidAmount: number;
  currency: string;
  issueDate: string | null;
  dueDate: string | null;
  overdue: boolean;
  project: { id: string; name: string } | null;
  s3Key: string | null;
  s3Url: string | null;
}

export interface Client {
  id: string;
  name: string;
  shortName: string | null;
  cifCui: string | null;
  regCom: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  billingAddress: string | null;
  country: string | null;
  contactPerson: string | null;
  contactRole: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  notes: string | null;
  avatarUrl: string | null;
  color: string | null;
  isActive: boolean;
  // Multi-company: firm tag (NULL = default firm).
  companyId: string | null;
  company: { id: string; name: string } | null;
  // Industria (Medical, Dental…); same industry = competitors (Actori).
  industryId: string | null;
  industry: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

/** Echipa clientului: a colleague working on this client. */
export interface ClientMember {
  id: string;
  userId: string;
  role: string | null;
  user: { id: string; name: string; email: string; avatarUrl: string | null };
  createdAt: string;
}

export interface ClientDetail extends Client {
  defaultProjectId: string | null;
  members: ClientMember[];
  contacts: ClientContact[];
  projects: ClientProject[];
  resources: ClientResource[];
  meetingNotes: ClientMeetingNote[];
  financials: ClientFinancials;
  counts: {
    projects: number;
    contacts: number;
    resources: number;
    meetingNotes: number;
  };
}

export interface ClientListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ClientListResponse {
  success: true;
  data: ClientSummary[];
  meta: ClientListMeta;
}

// Columns the list can be ordered by; "-field" = descending (mirrors backend).
export type ClientSortField =
  | 'name'
  | 'createdAt'
  | 'updatedAt'
  | 'cifCui'
  | 'isActive'
  | 'projectCount'
  | 'totalInvoiced'
  | 'outstanding'
  | 'totalReceived';
export type ClientSortToken = ClientSortField | `-${ClientSortField}`;

export interface ClientFilters {
  search?: string;
  active?: 'true' | 'false';
  hasProjects?: 'true' | 'false';
  // Firm scope (multi-company): undefined = all firms.
  companyId?: string;
  industryId?: string;
  sort?: ClientSortToken;
  page?: number;
  limit?: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

export interface ClientInput {
  name: string;
  shortName?: string;
  cifCui?: string;
  regCom?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  billingAddress?: string;
  country?: string;
  contactPerson?: string;
  contactRole?: string;
  contactEmail?: string;
  contactPhone?: string;
  notes?: string;
  avatarUrl?: string;
  color?: string | null;
  isActive?: boolean;
  // Multi-company: firm tag; null = default firm.
  companyId?: string | null;
  // Industria; null = not set.
  industryId?: string | null;
}

export interface ContactInput {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
  isPrimary?: boolean;
}

export interface UploadUrlResult {
  uploadUrl: string;
  key: string;
  expiresIn?: number;
}

export interface SeedResult {
  created: number;
  linkedInvoices: number;
  linkedSmartbill: number;
  linkedProjects: number;
  clients: { id: string; name: string; cifCui: string | null }[];
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQuery(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Clients ---------------------------------------------------------------

export async function listClients(filters: ClientFilters = {}): Promise<ClientListResponse> {
  return apiFetch<ClientListResponse>(
    `/api/clients${buildQuery(filters as Record<string, unknown>)}`,
    { headers: authHeaders() },
  );
}

export async function getClient(id: string): Promise<ClientDetail> {
  const res = await apiFetch<Envelope<ClientDetail>>(`/api/clients/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createClient(input: ClientInput): Promise<Client> {
  const res = await apiFetch<Envelope<Client>>('/api/clients', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateClient(id: string, input: ClientInput): Promise<Client> {
  const res = await apiFetch<Envelope<Client>>(`/api/clients/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteClient(id: string): Promise<Client> {
  const res = await apiFetch<Envelope<Client>>(`/api/clients/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

/** Soft-deletes a batch of clients (isActive=false); unknown ids are skipped. */
export async function bulkDeleteClients(
  ids: string[],
): Promise<{ requested: number; deactivated: number }> {
  const res = await apiFetch<Envelope<{ requested: number; deactivated: number }>>(
    '/api/clients/bulk-delete',
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ids }) },
  );
  return res.data;
}

export async function seedClientsFromInvoices(): Promise<SeedResult> {
  const res = await apiFetch<Envelope<SeedResult>>('/api/clients/seed-from-invoices', {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

// --- Contacts --------------------------------------------------------------

export async function createContact(clientId: string, input: ContactInput): Promise<ClientContact> {
  const res = await apiFetch<Envelope<ClientContact>>(`/api/clients/${clientId}/contacts`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateContact(
  clientId: string,
  contactId: string,
  input: ContactInput,
): Promise<ClientContact> {
  const res = await apiFetch<Envelope<ClientContact>>(
    `/api/clients/${clientId}/contacts/${contactId}`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(input),
    },
  );
  return res.data;
}

export async function deleteContact(clientId: string, contactId: string): Promise<void> {
  await apiFetch<Envelope<{ id: string; deleted: boolean }>>(
    `/api/clients/${clientId}/contacts/${contactId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

// --- Invoices --------------------------------------------------------------

export interface ClientInvoicesResponse {
  success: true;
  data: AggregatedInvoice[];
  meta: { totals: ClientFinancials };
}

export async function getClientInvoices(clientId: string): Promise<ClientInvoicesResponse> {
  return apiFetch<ClientInvoicesResponse>(`/api/clients/${clientId}/invoices`, {
    headers: authHeaders(),
  });
}

// --- Resources -------------------------------------------------------------

export async function requestResourceUploadUrl(
  clientId: string,
  input: { fileName: string; category: ResourceCategory; contentType: string; fileSize?: number },
): Promise<UploadUrlResult> {
  const res = await apiFetch<Envelope<UploadUrlResult>>(
    `/api/clients/${clientId}/resources/upload-url`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function createResource(
  clientId: string,
  input: {
    name: string;
    category: ResourceCategory;
    s3Key: string;
    description?: string;
    fileType?: string;
    fileSize?: number;
  },
): Promise<ClientResource> {
  const res = await apiFetch<Envelope<ClientResource>>(`/api/clients/${clientId}/resources`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteResource(clientId: string, resourceId: string): Promise<void> {
  await apiFetch<Envelope<{ id: string; deleted: boolean }>>(
    `/api/clients/${clientId}/resources/${resourceId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

// Meeting-note API moved to the polymorphic `meetingNotesApi` module (CLE-205)
// backed by the unified `/api/meeting-notes` router. The `ClientMeetingNote`
// type and `ClientDetail.meetingNotes` are retained because the client detail
// payload still embeds them.

// --- Shared: direct S3 upload ----------------------------------------------

// Upload raw bytes to a presigned S3 URL (not our API, so use raw fetch).
export async function uploadToSignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: UploadProgressHandler,
): Promise<void> {
  await putFileWithProgress(uploadUrl, file, contentType, onProgress);
}

// --- Echipa clientului + default container ---------------------------------

export async function addClientMember(
  clientId: string,
  input: { userId: string; role?: string | null },
): Promise<ClientMember[]> {
  const res = await apiFetch<Envelope<ClientMember[]>>(`/api/clients/${clientId}/members`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateClientMember(
  clientId: string,
  memberId: string,
  role: string | null,
): Promise<ClientMember[]> {
  const res = await apiFetch<Envelope<ClientMember[]>>(
    `/api/clients/${clientId}/members/${memberId}`,
    { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ role }) },
  );
  return res.data;
}

export async function removeClientMember(
  clientId: string,
  memberId: string,
): Promise<ClientMember[]> {
  const res = await apiFetch<Envelope<ClientMember[]>>(
    `/api/clients/${clientId}/members/${memberId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

/** The client's own container project, created on first use. */
export async function ensureClientDefaultProject(
  clientId: string,
): Promise<{ id: string; code: string; name: string }> {
  const res = await apiFetch<Envelope<{ id: string; code: string; name: string }>>(
    `/api/clients/${clientId}/default-project`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

// --- Calendar de postări: the client's shared link --------------------------

export interface ShareLinkStatus {
  active: {
    createdAt: string;
    lastUsedAt: string | null;
    createdBy: { name: string } | null;
  } | null;
}

export async function getShareLink(clientId: string): Promise<ShareLinkStatus> {
  const res = await apiFetch<Envelope<ShareLinkStatus>>(`/api/clients/${clientId}/share-link`, {
    headers: authHeaders(),
  });
  return res.data;
}

/** A new link + PIN, returned once; the previous link stops working. */
export async function createShareLink(clientId: string): Promise<{ url: string; pin: string }> {
  const res = await apiFetch<Envelope<{ url: string; pin: string }>>(
    `/api/clients/${clientId}/share-link`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

export async function revokeShareLink(clientId: string): Promise<void> {
  await apiFetch<Envelope<unknown>>(`/api/clients/${clientId}/share-link`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}
